import { useFlag } from '@unleash/proxy-client-react'
import { useOrgGroupAccess } from '../useOrgGroupAccess'
import { FeatureAccessPath } from '@/types/configuration'
import { ClerkUserRole } from '@/types/auth'

type UseAssessmentsAccessOptions = {
  /**
   * Context determines which feature flag and restricted roles to use:
   * - 'learner': Uses 'learner-assessments' flag, restricts LEARNER and COACH
   * - 'admin': Uses 'admin-assessments-view' flag, restricts CLIENT_ADMIN and INSTRUCTOR
   */
  readonly context: 'learner' | 'admin'
}

type UseAssessmentsAccessReturn = {
  /**
   * Whether assessments are fully enabled (feature flag AND org/group access)
   */
  readonly isEnabled: boolean
  /**
   * Whether the access check is still loading
   */
  readonly isLoading: boolean
  /**
   * Whether the global feature flag is enabled
   */
  readonly featureFlagEnabled: boolean
  /**
   * Whether the org/group has assessments enabled
   */
  readonly orgGroupAccessEnabled: boolean
}

const CONTEXT_CONFIG = {
  learner: {
    featureFlag: 'learner-assessments',
    restrictedRoles: [ClerkUserRole.LEARNER, ClerkUserRole.COACH],
  },
  admin: {
    featureFlag: 'admin-assessments-view',
    restrictedRoles: [ClerkUserRole.CLIENT_ADMIN, ClerkUserRole.INSTRUCTOR],
  },
} as const

/**
 * Hook to check if assessments are accessible based on both feature flags and org/group configuration.
 *
 * For learner context:
 * - Checks 'learner-assessments' feature flag
 * - Checks org/group 'assessments.enabled' config for LEARNER and COACH roles
 *
 * For admin context:
 * - Checks 'admin-assessments-view' feature flag
 * - Checks org/group 'assessments.enabled' config for CLIENT_ADMIN and INSTRUCTOR roles
 * - SUPER_ADMIN and ENGEN_ADMIN are not restricted by org/group config
 */
export function useAssessmentsAccess({ context }: UseAssessmentsAccessOptions): UseAssessmentsAccessReturn {
  const config = CONTEXT_CONFIG[context]

  // Get the global feature flag
  const featureFlagEnabled = useFlag(config.featureFlag)

  // Get org/group access (only applies to restricted roles)
  const { hasAccess: orgGroupAccessEnabled, isLoading } = useOrgGroupAccess({
    configPath: FeatureAccessPath.ASSESSMENTS_ENABLED,
    restrictedRoles: [...config.restrictedRoles],
  })

  // Combine both checks: feature must be enabled globally AND org must have access
  const isEnabled = featureFlagEnabled && orgGroupAccessEnabled

  return {
    isEnabled,
    isLoading,
    featureFlagEnabled,
    orgGroupAccessEnabled,
  }
}
